/**
 * EntitySheet — Unified sheet/modal component for all entity types.
 *
 * TWO variants:
 *   - "sheet" (default): Full modal with header, body, optional footer
 *   - "dialog": Compact centered alert for confirmations (replaces ConfirmationDialog)
 *
 * All styling uses ds tokens — no hardcoded values.
 *
 * Usage (Form Sheet):
 *   <EntitySheet
 *     isOpen={isOpen}
 *     onClose={onClose}
 *     title="Add Vehicle"
 *     footer={<>
 *       <Button variant="secondary" onClick={onClose}>Cancel</Button>
 *       <Button onClick={onSave}>Add Vehicle</Button>
 *     </>}
 *   >
 *     <TextField label="Year" ... />
 *   </EntitySheet>
 *
 * Usage (Confirmation Dialog):
 *   <EntitySheet
 *     variant="dialog"
 *     isOpen={showDelete}
 *     onClose={() => setShowDelete(false)}
 *     title='Delete "123 Main St"?'
 *     message="This action can't be undone."
 *     confirmLabel="Delete"
 *     onConfirm={handleDelete}
 *   />
 */

import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { ds } from '../tokens';
import { Tooltip, Button } from '../primitives';
import { useFocusTrap } from '../../shared/utils/accessibility';
import type { BadgeColor } from './EntityCard';

// ── Badge colors (shared with EntityCard) ────────────────────────
const badgeColors: Record<BadgeColor, { bg: string; text: string }> = {
  blue: { bg: ds.color.chip.blue.bg, text: ds.color.chip.blue.text },
  green: { bg: ds.color.chip.green.bg, text: ds.color.chip.green.text },
  orange: { bg: ds.color.chip.orange.bg, text: ds.color.chip.orange.text },
  purple: { bg: ds.color.chip.purple.bg, text: ds.color.chip.purple.text },
  gray: { bg: ds.color.chip.gray.bg, text: ds.color.chip.gray.text },
  red: { bg: ds.color.chip.red.bg, text: ds.color.chip.red.text },
  teal: { bg: ds.color.chip.teal.bg, text: ds.color.chip.teal.text },
};

// Re-export for backwards compatibility; actual singleton in escStack.ts
import { escStack } from '../escStack';
export { escStack };

// ── Size classes ────────────────────────────────────────────────

const sizeClasses: Record<string, string> = {
  sm:   'max-w-sm',
  md:   'max-w-[30rem]',
  lg:   'max-w-[42rem]',
  xl:   'max-w-3xl',
  full: 'max-w-[75rem]',
};

// ── Header action type ──────────────────────────────────────────

export interface SheetHeaderAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

// ── Component ───────────────────────────────────────────────────

export interface EntitySheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Close handler (also Cancel for dialog variant) */
  onClose: () => void;
  /** Sheet title */
  title: string;
  /** Variant: "sheet" (default) or "dialog" (compact confirmation) */
  variant?: 'sheet' | 'dialog';
  // ── Sheet variant props ───────────────────────────────────────
  /** Optional icon before title */
  icon?: React.ReactNode;
  /** Badge chip next to title (uses same styling as EntityCard badges) */
  badge?: { label: string; color: BadgeColor };
  /** Additional badges after the primary badge */
  badges?: { label: string; color: BadgeColor }[];
  /** Content after title (badges, chips) */
  titleTrailing?: React.ReactNode;
  /** Header action button (e.g., "+ New Link") */
  headerAction?: SheetHeaderAction;
  /** Custom header actions (e.g., Edit icon) — rendered before headerAction */
  actions?: React.ReactNode;
  /** Clear/reset form handler — renders a clear icon before the close button */
  onClear?: () => void;
  /** Sheet body content */
  children?: React.ReactNode;
  /** Custom footer content (buttons) — omit for list-only sheets */
  footer?: React.ReactNode;
  /** Footer alignment */
  footerAlign?: 'start' | 'end';
  /** Back navigation */
  onBack?: () => void;
  backLabel?: string;
  /** Sheet size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Hide backdrop overlay */
  noBackdrop?: boolean;
  /** Additional className */
  className?: string;
  // ── Form footer props (auto-renders Cancel + Submit) ─────────
  /** Form submit handler — when provided, auto-renders Cancel + Submit buttons */
  onSubmit?: () => void;
  /** Submit button label (default: "Save") */
  submitLabel?: string;
  /** Disable submit button */
  submitDisabled?: boolean;
  /** Cancel button label (default: "Cancel") */
  cancelLabel?: string;
  // ── Dialog variant props ──────────────────────────────────────
  /** Dialog message (below title) */
  message?: string;
  /** Confirm button label (default: "Delete") */
  confirmLabel?: string;
  /** Confirm button variant (default: "danger") */
  confirmVariant?: 'danger' | 'primary';
  /** Confirm callback */
  onConfirm?: () => void;
  /** When true, locks the sheet: disables close/ESC/backdrop click and dims the body */
  loading?: boolean;
  /** When true, disables close/ESC/backdrop click WITHOUT dimming the body */
  preventClose?: boolean;
}

export function EntitySheet({
  isOpen,
  onClose,
  title,
  variant = 'sheet',
  // Sheet props
  icon,
  badge,
  badges = [],
  titleTrailing,
  headerAction,
  actions,
  onClear,
  children,
  footer,
  footerAlign = 'end',
  onBack,
  backLabel,
  size = 'md',
  noBackdrop = false,
  className = '',
  // Form footer props
  onSubmit,
  submitLabel = 'Save',
  submitDisabled = false,
  cancelLabel = 'Cancel',
  // Dialog props
  message,
  confirmLabel = 'Delete',
  confirmVariant = 'danger',
  onConfirm,
  loading = false,
  preventClose = false,
}: EntitySheetProps) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const focusTrapRef = useFocusTrap(isOpen);
  const setFocusTrapRef = useCallback((node: HTMLDivElement | null) => {
    (focusTrapRef as { current: HTMLDivElement | null }).current = node;
  }, [focusTrapRef]);

  const isDialog = variant === 'dialog';

  // Track locked state in a ref so the ESC handler always sees current value
  const lockedRef = useRef(loading || preventClose);
  lockedRef.current = loading || preventClose;

  // Handle ESC key and body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const closeFn = () => { if (!lockedRef.current) onCloseRef.current(); };
    escStack.push(closeFn);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // For dialogs, add class to hide parent sheet backdrops
    if (isDialog) document.body.classList.add('has-dialog');
    return () => {
      const idx = escStack.indexOf(closeFn);
      if (idx !== -1) escStack.splice(idx, 1);
      document.body.style.overflow = prevOverflow;
      if (isDialog) document.body.classList.remove('has-dialog');
    };
  }, [isOpen, isDialog]);

  // Auto-focus Cancel button for dialogs
  useEffect(() => {
    if (isOpen && isDialog) {
      requestAnimationFrame(() => cancelRef.current?.focus());
    }
  }, [isOpen, isDialog]);

  if (!isOpen) return null;

  // ── Dialog variant ────────────────────────────────────────────
  if (isDialog) {
    return createPortal(
      <div className={`fixed inset-0 ${ds.zIndex.sheet} flex items-center justify-center p-4`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 ${ds.color.fill.overlay}`}
          style={{ animation: 'sheet-backdrop-in 250ms ease-out both' }}
        />
        {/* Dialog */}
        <div
          ref={setFocusTrapRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          className={[
            'relative w-full max-w-sm',
            ds.color.fill.elevated,
            ds.radius.xl,
            ds.shadow.sheet,
            'p-6 text-center',
            'animate-in fade-in zoom-in-[0.96] duration-200 ease-out',
          ].join(' ')}
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              {/* Apple-style 8-segment spinner */}
              <svg
                className="animate-spin mb-4"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                style={{ color: ds.color.raw.labelSecondary }}
              >
                {Array.from({ length: 8 }, (_, i) => {
                  const angle = i * 45;
                  const opacity = 0.15 + (i / 8) * 0.85;
                  return (
                    <line
                      key={i}
                      x1="12"
                      y1="4"
                      x2="12"
                      y2="8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      opacity={opacity}
                      transform={`rotate(${angle} 12 12)`}
                    />
                  );
                })}
              </svg>
              <p
                className={`${ds.type.body} ${ds.type.weight.medium}`}
                style={{ color: ds.color.raw.nearBlack }}
              >
                {confirmLabel === 'Delete' ? 'Deleting...' : confirmLabel === 'Remove' ? 'Removing...' : `${confirmLabel}...`}
              </p>
              <p
                className={`${ds.type.footnote} mt-1`}
                style={{ color: ds.color.raw.systemGray }}
              >
                This may take a moment
              </p>
            </div>
          ) : (
            <>
              <h3 id="confirm-dialog-title" className={`${ds.type.headline} ${ds.color.label.primary}`}>
                {title}
              </h3>
              {message && (
                <p className={`${ds.type.body} ${ds.color.label.secondary} mt-2`}>
                  {message}
                </p>
              )}
              {children}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={onClose}
                  buttonRef={cancelRef}
                >
                  {cancelLabel}
                </Button>
                <Button
                  variant={confirmVariant}
                  className="flex-1"
                  onClick={() => onConfirm?.()}
                >
                  {confirmLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>,
      document.body,
    );
  }

  // ── Sheet variant ─────────────────────────────────────────────
  return createPortal(
    <div className={['fixed inset-0 flex items-center justify-center p-4', ds.zIndex.sheet].join(' ')}>
      {/* Backdrop */}
      <div
        className={['absolute inset-0 sheet-backdrop', noBackdrop ? '' : ds.color.fill.overlay].join(' ')}
        style={{ animation: 'sheet-backdrop-in 250ms ease-out both' }}
      />

      {/* Card */}
      <div
        ref={setFocusTrapRef}
        role="dialog"
        aria-modal="true"
        className={[
          'relative w-full',
          sizeClasses[size],
          ds.color.fill.elevated,
          ds.radius.xl,
          ds.shadow.sheet,
          'flex flex-col max-h-[75vh] p-2',
          className,
        ].join(' ')}
        style={{ animation: 'sheet-card-in 250ms ease-out both' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="px-5 py-4 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-y-2">
            <div className="flex items-center gap-2.5 min-w-0">
              {icon && <div className={ds.color.label.secondary}>{icon}</div>}
              <h2 className={[ds.type.title3, ds.type.weight.semibold, ds.color.label.primary, 'truncate'].join(' ')}>
                {title}
              </h2>
              {badge && (
                <span
                  className={ds.color.chip.base}
                  style={{ backgroundColor: badgeColors[badge.color].bg, color: badgeColors[badge.color].text }}
                >
                  {badge.label}
                </span>
              )}
              {badges.map((b, i) => (
                <span
                  key={i}
                  className={ds.color.chip.base}
                  style={{ backgroundColor: badgeColors[b.color].bg, color: badgeColors[b.color].text }}
                >
                  {b.label}
                </span>
              ))}
              {titleTrailing}
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Custom actions (Edit icon, etc.) */}
              {actions}
              {/* Header action (+ New Link, + Add Contact, etc.) */}
              {headerAction && (
                <button
                  type="button"
                  onClick={headerAction.onClick}
                  className={[
                    'flex items-center gap-1',
                    ds.type.bodySmall,
                    ds.type.weight.medium,
                    ds.transition.fast,
                    'hover:underline',
                  ].join(' ')}
                  style={{ color: ds.color.raw.systemBlue }}
                >
                  {headerAction.icon || <Plus className={ds.icon.sm} />}
                  {headerAction.label}
                </button>
              )}
              {/* Clear form button */}
              {onClear && (
                <Tooltip text="Clear form">
                  <button
                    type="button"
                    onClick={onClear}
                    className={[
                      'inline-flex items-center justify-center',
                      ds.transition.fast,
                    ].join(' ')}
                    aria-label="Clear form"
                  >
                    <svg
                      className={[ds.icon.md, ds.interaction.actionIcon].join(' ')}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M20.025 4.853v4.992" />
                    </svg>
                  </button>
                </Tooltip>
              )}
              {/* Close button */}
              <Tooltip text="Close">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading || preventClose}
                  className={[
                    'inline-flex items-center justify-center',
                    ds.transition.fast,
                    (loading || preventClose) ? 'opacity-30 cursor-not-allowed' : '',
                  ].join(' ')}
                  aria-label="Close"
                >
                  <svg
                    className={[ds.icon.md, ds.interaction.actionIcon].join(' ')}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div
          className={['flex-1 overflow-y-auto px-5 py-4', ds.layout.scrollbar, loading ? 'pointer-events-none' : ''].join(' ')}
          style={loading ? { opacity: 0.5, transition: 'opacity 200ms ease' } : { transition: 'opacity 200ms ease' }}
        >
          {children}
        </div>

        {/* ── Footer (optional) ──────────────────────────────── */}
        {/* Render footer if: custom footer, back navigation, or form submit handler */}
        {(footer || onSubmit || (onBack && backLabel)) && (
          <div
            className={[
              'flex flex-wrap items-center px-5 py-3 flex-shrink-0 gap-3',
              onBack && backLabel ? 'justify-between' : (footerAlign === 'end' ? 'justify-end' : 'justify-start'),
            ].join(' ')}
          >
            {/* Back link */}
            {onBack && backLabel && (
              <button
                type="button"
                onClick={onBack}
                className={[
                  'flex items-center gap-1',
                  ds.type.body,
                  ds.transition.fast,
                  'hover:underline',
                ].join(' ')}
                style={{ color: ds.color.raw.systemBlue }}
              >
                <svg
                  className={ds.icon.xs}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                {backLabel}
              </button>
            )}
            {/* Custom footer buttons */}
            {footer && (
              <div className="flex flex-wrap items-center gap-3">
                {footer}
              </div>
            )}
            {/* Auto form footer: Cancel + Submit (only if no custom footer) */}
            {!footer && onSubmit && (
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="secondary" onClick={onClose}>{cancelLabel}</Button>
                <Button variant="primary" onClick={onSubmit} disabled={submitDisabled}>{submitLabel}</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
